import { useMutation } from "@tanstack/react-query";
import { ChevronsUpDownIcon, LogOutIcon, ShieldIcon, UserIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { displayName, hasAccounts, publicDemo, useMe } from "@/lib/account";
import { section } from "@/lib/garage";
import { useTRPC } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { Avatar } from "./avatar";

/** Signs out and opens the sign-in page, or in the public demo its request page. */
export function useSignOut() {
  const trpc = useTRPC();
  const signOut = useMutation({
    ...trpc.account.signOut.mutationOptions(),
    meta: { silent: true },
  });
  return async () => {
    await signOut.mutateAsync().catch(() => undefined);
    window.location.assign(publicDemo() ? "/account/demo" : "/account/sign-in");
  };
}

/**
 * With accounts: the signed-in user with Profile, Admin (for admins) and Sign out. At the bottom
 * of the sidebar, or as an avatar button in the phone's top bar.
 */
export function UserMenu({
  variant = "sidebar",
  className,
}: {
  variant?: "sidebar" | "icon";
  className?: string;
}) {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const signOut = useSignOut();
  if (!hasAccounts() || !me) return null;
  const name = displayName(me);
  // A visitor has no email to show (ADR 0012).
  const detail = me.demo ? t("demo.visitor") : me.email;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {variant === "sidebar" ? (
          <button
            type="button"
            className={cn(
              "flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left outline-none hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring/40",
              className,
            )}
          >
            <Avatar user={me} />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium">{name}</span>
              <span className="block truncate text-xs text-muted-foreground">{detail}</span>
            </span>
            <ChevronsUpDownIcon className="size-4 text-muted-foreground" />
          </button>
        ) : (
          <button
            type="button"
            aria-label={t("account.menu")}
            className={cn(
              "flex size-9 items-center justify-center rounded-lg outline-none hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring/40",
              className,
            )}
          >
            <Avatar user={me} className="size-7" />
          </button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={variant === "sidebar" ? "start" : "end"}
        side={variant === "sidebar" ? "top" : "bottom"}
        className={variant === "sidebar" ? "w-(--radix-dropdown-menu-trigger-width)" : "w-60"}
      >
        <DropdownMenuLabel className="flex flex-col">
          <span className="truncate text-foreground">{name}</span>
          <span className="truncate font-normal">{detail}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <a href="/account/profile">
            <UserIcon />
            {t("account.profile")}
          </a>
        </DropdownMenuItem>
        {me.platformRole === "admin" && section !== "admin" ? (
          <DropdownMenuItem asChild>
            <a href="/admin">
              <ShieldIcon />
              {t("account.admin")}
            </a>
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => void signOut()}>
          <LogOutIcon />
          {t("account.signOut")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
